import type { T } from './i18n';
import type { StringKey } from './strings';
import type { VehicleType } from './types';

/**
 * How each API vehicle type is shown. The owner's launch list is BIKE, AUTO and CAR (2026-10-20); CAR is shown as
 * "Car". Adding a type later = the server enum + a fare, then one entry here. Labels and nouns come from the
 * strings file (lib/strings.ts → `vehicle.*`) so they follow the rider's language.
 */
/** The drawing for each type is src/art/Vehicle.tsx (vector, no pictures). */
export type VehicleMeta = { seats: number; ac: boolean };

export const VEHICLES: Record<VehicleType, VehicleMeta> = {
  BIKE: { seats: 1, ac: false },
  AUTO: { seats: 3, ac: false },
  CAR: { seats: 4, ac: true },
};

export const VEHICLE_TYPES: VehicleType[] = ['BIKE', 'AUTO', 'CAR'];
/** Home grid order (Bike, Auto, Car). */
export const GRID_ORDER: VehicleType[] = ['BIKE', 'AUTO', 'CAR'];
/** Choose-ride list order (Auto, Bike, Car). */
export const LIST_ORDER: VehicleType[] = ['AUTO', 'BIKE', 'CAR'];

export const meta = (t: VehicleType) => VEHICLES[t] ?? VEHICLES.AUTO;
/** Only the types this app knows (a type the server adds later is ignored until it has an entry here). */
export const known = <T extends { vehicleType: VehicleType }>(items: T[]) => items.filter((i) => i.vehicleType in VEHICLES);
export const sortBy = <T extends { vehicleType: VehicleType }>(items: T[], order: VehicleType[]) =>
  known(items).sort((a, b) => order.indexOf(a.vehicleType) - order.indexOf(b.vehicleType));

/** The type's words in the rider's language: "Car" / "car" / "cars". */
export const words = (t: T, v: VehicleType) => ({
  label: t(`vehicle.${v}` as StringKey),
  noun: t(`noun.${v}` as StringKey),
  plural: t(`plural.${v}` as StringKey),
});

/** "3 seats", "4 seats · AC" */
export const seatsLine = (t: T, v: VehicleType) => {
  const m = meta(v);
  const seats = t.n('seats', m.seats);
  return m.ac ? t('seats.ac', { seats }) : seats;
};

/** "yellow-green auto" — the mockup words it colour first, lower case. */
export const colourNoun = (t: T, colour: string | undefined, v: VehicleType) => `${(colour ?? '').trim().toLowerCase()} ${words(t, v).noun}`.trim();
